import { ColorPicker } from '@/components/inputs/ColorPicker';
import { ImagePicker } from '@/components/inputs/ImagePicker/ImagePicker';
import { BUILTIN_IMG_SRC } from '@/components/inputs/ImagePicker/source';
import { DEFAULT_FONT_FAMILY, FONT_FAMILIES, Z_INDEX_UI } from '@/constant';
import { ResizeBorder } from '@/pages/LessonEditor/components/ResizeBorder';
import { Coordinate, isSlideComponentMCAnswer, PropsWithPreview, Size, SlideComponentMCAnswer } from '@/types/slide';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import FlipIcon from '@mui/icons-material/Flip';
import FormatColorFillIcon from '@mui/icons-material/FormatColorFill';
import HideImageIcon from '@mui/icons-material/HideImage';
import ImageIcon from '@mui/icons-material/Image';
import TitleIcon from '@mui/icons-material/Title';
import { Box, Popover, Radio } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ComponentLabel } from '../ComponentLabel';
import { ContextualBar } from '../ContextualBar';
import { useRightClickMenu } from '../hooks/useRightClickMenu';
import { useSlideComponent } from '../hooks/useSlideComponent';
import { ImageContent } from '../image/ImageContent';
import { RightClickMenu } from '../RightClickMenu';
import { TextContent } from '../SlideComponentText/TextContent';
import { TypeSettingsPopover } from '../SlideComponentText/TypeSettingsPopover';

import { useOutsideClick } from '@/hooks/useOutsideClick';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { useDebounce } from 'rooks';
import { SlideRef } from '../Slide';

interface Props {
    init: {
        focus?: boolean;
    };
    model: SlideComponentMCAnswer;
    onDeleteObject?: (id: string) => void;
    onUpdateObject?: (id: string, component: SlideComponentMCAnswer) => void;
    onCheckObject?: (id: string) => void;
    onUploadImage?: (file: File) => Promise<string>;
    onBringToFrontHandler?: (id: string) => void;
    onBringToBackHandler?: (id: string) => void;
    onUndo?: (id: string) => void;
    onRedo?: (id: string) => void;
    baseSize?: Size;
    answerIndex?: number;
    answerCount?: number;
    selectedResult?: { isSelected: boolean; isAnswer: boolean };
    slideRef: React.RefObject<SlideRef | null>;
}

export const SlideComponentMCAnswerObject: React.FC<PropsWithPreview<Props>> = ({
    init,
    model,
    preview,
    onDeleteObject,
    onUpdateObject,
    onCheckObject,
    onUploadImage,
    onBringToBackHandler,
    onBringToFrontHandler,
    onUndo,
    onRedo,
    baseSize,
    answerIndex,
    answerCount,
    selectedResult,
    slideRef,
}) => {
    const { t } = useTranslation();

    const { rightClickMenu, handleRightClickMenu, rightClickMenuClose } = useRightClickMenu();

    const mainRef = useRef<HTMLDivElement | null>(null);

    // Add refs for the popovers
    const imagePickerRef = useRef<HTMLDivElement | null>(null);
    const typeSettingsRef = useRef<HTMLDivElement | null>(null);
    const backgroundColorPickerRef = useRef<HTMLDivElement | null>(null);

    const { isSelected } = useSlideComponent(mainRef, [], !!preview);

    const [isFocus, setIsFocus] = useState<boolean>(init.focus ?? false);

    const [tSize, setTSize] = useState<Size>({ width: 100, height: 100 });
    const [tPos, setTPos] = useState<Coordinate>({ x: 0, y: 0 });

    // Add state for image picker popover
    const [imagePickerAnchorEl, setImagePickerAnchorEl] = useState<HTMLElement | null>(null);
    const [typeSettingsAnchorEl, setTypeSettingsAnchorEl] = useState<HTMLElement | null>(null);
    const [backgroundColorAnchorEl, setBackgroundColorAnchorEl] = useState<HTMLElement | null>(null);
    const openBackgroundColorPicker = Boolean(backgroundColorAnchorEl);

    const [aspectRatio] = useState(tSize.width / tSize.height);

    const isEditing = isFocus || isSelected;

    const debouncedUpdateObject = useDebounce(onUpdateObject || (() => {}), 500);

    // Function to calculate answer position and size based on baseSize and index
    const calculateAnswerSizeAndPosition = (baseSize: Size, index: number, answerCount: number) => {
        const { width: baseWidth, height: baseHeight } = baseSize;
        const margin = 16; // 16px margin for each object
        const numColumns = answerCount > 4 ? 3 : 2;

        // Calculate component dimensions with margin
        const componentWidth = (baseWidth - margin * (numColumns + 1)) / numColumns;
        const numRows = Math.ceil(answerCount / numColumns);
        const componentHeight = (baseHeight - margin * (numRows + 1)) / numRows;

        // Calculate position based on index with margin
        const row = Math.floor(index / numColumns);
        const col = index % numColumns;

        const x = margin + col * (componentWidth + margin);
        const y = margin + row * (componentHeight + margin);

        return {
            width: componentWidth,
            height: componentHeight,
            x,
            y,
        };
    };

    useOutsideClick(mainRef, () => {
        if (preview) return;
        if (!mainRef) return;

        setIsFocus(false);
    });

    useEffect(() => {
        if (isFocus && !preview) {
            setImagePickerAnchorEl(mainRef.current);
        }
    }, [isFocus, preview]);

    // Set position and size based on baseSize and answerIndex for display
    useEffect(() => {
        if (baseSize && answerIndex !== undefined) {
            const position = calculateAnswerSizeAndPosition(baseSize, answerIndex, answerCount ?? 1);
            setTSize({ width: position.width, height: position.height });
            setTPos({ x: position.x, y: position.y });
        }
    }, [baseSize, answerIndex, answerCount]);

    if (!isSlideComponentMCAnswer(model)) {
        return null;
    }

    const addImageHandler = () => {
        const updateModel = { ...model, type: 'image' as const, content: { image: '', imageURL: '', flip: 1 } } as SlideComponentMCAnswer;
        onUpdateObject?.(model.id, { ...updateModel });
    };

    const addTextHandler = () => {
        const updateModel: SlideComponentMCAnswer = {
            ...model,
            type: 'text' as const,
            content: {
                text: '',
                fontSize: 16,
                jyutping: [],
                color: '#000000',
                typeSettings: {
                    fontFamily: FONT_FAMILIES[0]?.fontFamily ?? DEFAULT_FONT_FAMILY,
                    formatting: [],
                    alignment: 'left',
                    isPhonetic: false,
                    borderRadius: 0,
                    backgroundColor: 'transparent',
                },
            },
        };
        onUpdateObject?.(model.id, updateModel);
    };

    const showAnswerInPreviewMode = typeof preview === 'object' && preview.answer;

    return (
        <Box
            onContextMenu={isEditing ? handleRightClickMenu : undefined}
            ref={mainRef}
            id={`mc-answer-component-${model.id}`}
            sx={{
                position: 'absolute',
                left: tPos.x,
                top: tPos.y,
                m: 'inherit',
                p: 'inherit',
                width: tSize.width,
                height: tSize.height,
            }}
            onClick={(e) => {
                if (!preview) {
                    e.stopPropagation();
                }
            }}
            onDoubleClick={(e) => {
                if (preview) return;
                e.stopPropagation();

                if (model.type === 'image') {
                    setImagePickerAnchorEl(mainRef.current);
                } else if (model.type === 'text') {
                    setTypeSettingsAnchorEl(mainRef.current);
                }
            }}
        >
            <ResizeBorder
                minSize={{ width: 80, height: 40 }}
                size={tSize}
                pos={tPos}
                preview={preview}
                isSelected={isEditing}
                aspectRatio={model.type === 'text' ? 0 : aspectRatio} // free resize for text
                resizable={false}
                disabled
                slideRef={slideRef}
            >
                <Box
                    sx={(theme) => ({
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        outline: '4px solid',
                        outlineColor:
                            selectedResult == undefined
                                ? (!preview || (typeof preview === 'object' && preview.answer)) && model.settings?.isAnswer
                                    ? (theme.vars || theme).palette.success.main
                                    : 'grey.500'
                                : undefined,
                        borderRadius: 0.1,
                        backgroundColor:
                            model.type === 'text' && model.content?.typeSettings?.backgroundColor && model.content?.typeSettings?.backgroundColor !== 'transparent'
                                ? (model.content?.typeSettings?.backgroundColor ?? 'transparent')
                                : (model.settings?.backgroundColor ?? 'transparent'),
                    })}
                >
                    <Box
                        sx={{
                            flex: 1,
                            height: '100%',
                            overflow: 'hidden',
                        }}
                    >
                        {!preview && (
                            <ComponentLabel
                                variant='caption'
                                sx={{
                                    ...(!isEditing && {
                                        opacity: 0, // Hide by default
                                        transition: 'opacity 0.2s ease',
                                        '#md-answer-component:hover &': {
                                            opacity: 1, // Show when text-component is hovered
                                        },
                                    }),
                                }}
                                noWrap
                            >
                                {t('slide.components.mc.answerObject')}
                            </ComponentLabel>
                        )}
                        {model.type === 'image' && (
                            <Box
                                sx={{
                                    height: '100%',
                                    aspectRatio: model.size.width / model.size.height,
                                }}
                            >
                                <ImageContent model={model} />
                            </Box>
                        )}
                        {model.type === 'text' && (
                            <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', pl: 2 }}>
                                <TextContent
                                    preview={preview}
                                    autoFocus={isEditing}
                                    model={model}
                                    onUpdate={(v) => {
                                        const updatedContent = { ...model.content, text: v };
                                        debouncedUpdateObject?.(model.id, { ...model, content: updatedContent });
                                    }}
                                />
                            </Box>
                        )}
                    </Box>

                    {/* answer slide in result page */}
                    {selectedResult?.isSelected && (
                        <>
                            <Box
                                sx={(theme) => ({
                                    position: 'absolute',
                                    borderRadius: '50%',
                                    top: -24,
                                    left: -24,
                                    width: 48,
                                    height: 48,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'white',
                                    border: '4px solid',
                                    borderColor: 'white',
                                    backgroundColor: selectedResult.isAnswer ? (theme.vars || theme).palette.success.main : (theme.vars || theme).palette.error.main,
                                    zIndex: Z_INDEX_UI + 1,
                                })}
                            >
                                {selectedResult.isAnswer ? (
                                    <CheckIcon
                                        sx={{
                                            color: 'white',
                                        }}
                                    />
                                ) : (
                                    <CloseIcon
                                        sx={{
                                            color: 'white',
                                        }}
                                    />
                                )}
                            </Box>
                        </>
                    )}
                    {model.settings?.isAnswer && (!preview || showAnswerInPreviewMode) && selectedResult === undefined && (
                        <Box
                            sx={(theme) => ({
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                px: 1,
                                py: 0.5,
                                color: 'white',
                                backgroundColor: (theme.vars || theme).palette.success.main,
                                zIndex: Z_INDEX_UI + 1,
                            })}
                        >
                            {t('slide.components.mc.correctAnswer')}
                        </Box>
                    )}
                    <Box sx={{ ml: 1, pointerEvents: preview || showAnswerInPreviewMode ? 'none' : 'auto' }}>
                        <Radio
                            checked={selectedResult === undefined ? (!preview || showAnswerInPreviewMode ? model.settings?.isAnswer : false) : selectedResult.isSelected ? true : false}
                            onChange={() => {
                                if (preview || showAnswerInPreviewMode) {
                                    return;
                                }
                                onCheckObject?.(model.id);
                            }}
                            sx={(theme) => ({
                                mr: 1,
                                '& .MuiSvgIcon-root': {
                                    fontSize: 32,
                                },
                                '&.Mui-checked': {
                                    color: selectedResult === undefined ? (theme.vars || theme).palette.primary.main : (theme.vars || theme).palette.secondary.main,
                                },
                            })}
                        />
                    </Box>
                </Box>

                {!preview && isEditing && (
                    <ContextualBar
                        anchor='bottom'
                        startButtons={[
                            ...(model.type === 'image'
                                ? [
                                      {
                                          icon: <EditIcon />,
                                          tooltip: t('slide.components.mc.tooltip.edit'),
                                          onClick: () => {
                                              setImagePickerAnchorEl(mainRef.current);
                                          },
                                      },
                                      {
                                          icon: <FlipIcon />,
                                          tooltip: t('slide.components.mc.tooltip.flip'),
                                          onClick: () => {
                                              const updatedContent = { ...model.content, flip: model.content.flip === 1 ? -1 : 1 };
                                              const updateModel = { ...model, content: updatedContent };
                                              onUpdateObject?.(model.id, updateModel);
                                          },
                                      },
                                      {
                                          icon: <HideImageIcon />,
                                          tooltip: t('slide.components.mc.tooltip.hideImage'),
                                          onClick: () => {
                                              addTextHandler();
                                          },
                                      },
                                  ]
                                : []),
                            ...(model.type === 'text'
                                ? [
                                      ...(model.content.text === ''
                                          ? [
                                                {
                                                    icon: <ImageIcon />,
                                                    tooltip: t('slide.components.mc.addImage'),
                                                    onClick: () => {
                                                        addImageHandler();
                                                        setImagePickerAnchorEl(mainRef.current);
                                                    },
                                                },
                                            ]
                                          : []),
                                      {
                                          icon: <TitleIcon />,
                                          tooltip: t('slide.components.assets.text.tooltip.typeSettings'),
                                          onClick: () => {
                                              setTypeSettingsAnchorEl(mainRef.current);
                                          },
                                      },
                                  ]
                                : []),
                            {
                                icon: <FormatColorFillIcon />,
                                tooltip: t('slide.components.mc.tooltip.backgroundColor'),
                                onClick: () => {
                                    setBackgroundColorAnchorEl(mainRef.current);
                                },
                            },
                        ]}
                        endButtons={[
                            {
                                icon: <DeleteIcon />,
                                tooltip: t('slide.components.mc.tooltip.remove'),
                                onClick: () => {
                                    onDeleteObject?.(model.id);
                                },
                            },
                        ]}
                    />
                )}
                {model.type === 'image' && (
                    <ImagePicker
                        type='image'
                        title={t('slide.components.mc.title')}
                        librarySources={BUILTIN_IMG_SRC}
                        anchorEl={imagePickerAnchorEl}
                        slotProps={{
                            paper: {
                                ref: imagePickerRef, // Attach the ref to the Paper element
                            },
                        }}
                        value={{
                            value: model.content?.image,
                            imageURL: model.content?.imageURL,
                        }}
                        onClose={() => {
                            setImagePickerAnchorEl(null);
                            setIsFocus(false);
                        }}
                        onUpdate={(settings) => {
                            const updatedContent = { ...model.content, image: settings.value, imageURL: settings.imageURL };
                            const updateModel = { ...model, content: updatedContent, ...(settings.size ? { size: settings.size } : {}) };
                            onUpdateObject?.(model.id, updateModel);
                        }}
                        onUpload={onUploadImage}
                    />
                )}
                {model.type === 'text' && typeSettingsAnchorEl && (
                    <TypeSettingsPopover
                        anchorEl={typeSettingsAnchorEl}
                        slotProps={{
                            paper: {
                                ref: typeSettingsRef, // Attach the ref to the Paper element
                                sx: {
                                    p: 2,
                                },
                            },
                        }}
                        value={{
                            isPhonetic: model?.content?.typeSettings?.isPhonetic,
                            fontFamily: model?.content?.typeSettings?.fontFamily,
                            fontSize: model?.content?.fontSize,
                            formatting: model?.content?.typeSettings?.formatting,
                            alignment: model?.content?.typeSettings?.alignment,
                            color: model?.content?.color,
                            borderRadius: model?.content?.typeSettings?.borderRadius,
                            backgroundColor: model?.content?.typeSettings?.backgroundColor,
                        }}
                        onClose={() => {
                            setTypeSettingsAnchorEl(null);
                        }}
                        onUpdate={(settings) => {
                            const typeSettingsContent = {
                                isPhonetic: settings.isPhonetic,
                                fontFamily: settings.fontFamily,
                                alignment: settings.alignment,
                                formatting: settings.formatting,
                                borderRadius: settings.borderRadius,
                                backgroundColor: settings.backgroundColor,
                            };

                            const updatedContent = { ...model.content, fontSize: settings.fontSize, typeSettings: typeSettingsContent, color: settings.color };
                            debouncedUpdateObject?.(model.id, { ...model, content: updatedContent });
                        }}
                    />
                )}
                {openBackgroundColorPicker && (
                    <Popover
                        open={openBackgroundColorPicker}
                        anchorEl={backgroundColorAnchorEl}
                        onClose={() => setBackgroundColorAnchorEl(null)}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'center',
                        }}
                        transformOrigin={{
                            vertical: -8,
                            horizontal: 'center',
                        }}
                        slotProps={{
                            paper: {
                                ref: backgroundColorPickerRef, // Attach the ref to the Paper element
                            },
                        }}
                    >
                        <Box sx={{ p: 2 }}>
                            <ColorPicker
                                value={model.settings.backgroundColor}
                                onChange={(color: string) => {
                                    const updateModel = { ...model, settings: { ...model.settings, backgroundColor: color } };
                                    debouncedUpdateObject?.(model.id, updateModel);
                                }}
                            />
                        </Box>
                    </Popover>
                )}
            </ResizeBorder>

            <RightClickMenu
                componentId={model.id}
                contextMenu={rightClickMenu}
                handleClose={rightClickMenuClose}
                bringToFront={onBringToFrontHandler}
                bringToBack={onBringToBackHandler}
                undo={onUndo}
                redo={onRedo}
            />
        </Box>
    );
};
